import { mediaImageIndexQueryKey } from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { type ChangeEvent, type DragEvent, type ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
	Button,
	formatFileValidationError,
	LibraryLayout,
	logger,
	type MediaAsset,
	SearchFilter,
	SelectFilter,
	type SelectFilterOption,
	SubtleLoader,
	Tooltip,
	validateImageFile,
} from '../../../ui'
import {
	assetHubCollectionFilterAtom,
	assetHubSearchAtom,
	assetHubSelectedAssetIdAtom,
	assetHubSortAtom,
} from '../atoms'
import { useAssetHubStore } from '../store'
import type { AssetHubSort } from '../types'
import { useAssetHubData } from '../useAssetHubData'
import { AssetCard } from './asset-card'
import { AssetUploadOverlay, type PendingAssetUpload } from './asset-upload-overlay'
import { CollectionsIcon, FilterOption } from './filter-option'

const HIDDEN_COLLECTIONS = new Set(['custom-font'])

let nextTempId = 0

const SORT_OPTIONS: ReadonlyArray<SelectFilterOption<AssetHubSort>> = [
	{ id: 'date-desc', label: 'Newest first' },
	{ id: 'date-asc', label: 'Oldest first' },
	{ id: 'name-asc', label: 'A → Z' },
	{ id: 'name-desc', label: 'Z → A' },
]

function sortAssets(assets: MediaAsset[], sort: AssetHubSort): MediaAsset[] {
	const sorted = [...assets]
	switch (sort) {
		case 'date-desc':
			return sorted.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
		case 'date-asc':
			return sorted.sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
		case 'name-asc':
			return sorted.sort((a, b) => a.fileName.localeCompare(b.fileName))
		case 'name-desc':
			return sorted.sort((a, b) => b.fileName.localeCompare(a.fileName))
	}
}

export interface AssetGridProps {
	mode: 'page' | 'picker'
	showCloseButton?: boolean
	onClose?: () => void
	committalBar?: ReactNode | false
	allowDelete?: boolean
	onSelect?: (asset: MediaAsset) => void
}

export function AssetGrid({
	mode,
	showCloseButton = false,
	onClose,
	committalBar,
	allowDelete = false,
	onSelect,
}: AssetGridProps) {
	const { t } = useTranslation('common')
	const store = useAssetHubStore()
	const queryClient = useQueryClient()
	const { assets, organizationId, uploadMutation, isLoading } = useAssetHubData()

	const [pendingUploads, setPendingUploads] = useState<PendingAssetUpload[]>([])
	const pendingUploadsRef = useRef(pendingUploads)
	useEffect(() => {
		pendingUploadsRef.current = pendingUploads
	}, [pendingUploads])
	useEffect(() => {
		return () => {
			for (const upload of pendingUploadsRef.current) {
				URL.revokeObjectURL(upload.previewUrl)
			}
		}
	}, [])

	// Flipped when the user clicks "Send to background" on the overlay. The async
	// upload handler keeps running (mutations and cache refetch still complete) but
	// it skips the post-completion UX — no toasts, no scroll, no auto-select — so
	// the user truly gets the asset hub back without interruption.
	const dismissedRef = useRef(false)

	// When a batch finishes with errors, the overlay stays open so the user can read
	// them; the queued post-completion (scroll + auto-select) runs once they hit Done.
	const pendingPostCompletionRef = useRef<(() => void) | null>(null)

	// True for the entire async lifetime of a batch — from the moment processFiles
	// commits to running until its finally clause completes. The `pendingUploads`
	// state can't be trusted as a lock because Send-to-background clears it while
	// mutations + invalidation are still mid-flight; without this ref a second batch
	// could start, reset `dismissedRef`, and corrupt the original batch's cleanup.
	// `isInFlightBatch` mirrors the ref as state so the UI (Upload button, drag
	// affordance, sidebar status pill) can react to it.
	const inFlightBatchRef = useRef(false)
	const [isInFlightBatch, setIsInFlightBatch] = useState(false)

	// Aborts any in-flight PUTs when the AssetGrid unmounts (user closes the manager,
	// navigates away). Without this the XHR keeps streaming bytes to GCS for a tab
	// the user already left, wasting their uplink and orphaning the GCS object
	// because confirm can no longer fire from the dead JS context.
	const uploadAbortRef = useRef<AbortController | null>(null)
	useEffect(() => {
		return () => {
			uploadAbortRef.current?.abort()
		}
	}, [])

	const visibleAssets = useMemo(() => {
		return assets.filter((asset) => !HIDDEN_COLLECTIONS.has(asset.collection))
	}, [assets])

	const collections = useMemo<Record<string, number>>(() => {
		const counts: Record<string, number> = {}
		for (const asset of visibleAssets) {
			counts[asset.collection] = (counts[asset.collection] ?? 0) + 1
		}
		return Object.fromEntries(Object.entries(counts).sort(([a], [b]) => a.localeCompare(b)))
	}, [visibleAssets])

	const [collectionFilter, setCollectionFilter] = useAtom(assetHubCollectionFilterAtom, { store })
	const [sort, setSort] = useAtom(assetHubSortAtom, { store })
	const [search, setSearch] = useAtom(assetHubSearchAtom, { store })

	const filteredAssets = useMemo(() => {
		let result = collectionFilter
			? visibleAssets.filter((asset) => asset.collection === collectionFilter)
			: visibleAssets
		const query = search.trim().toLowerCase()
		if (query) {
			result = result.filter((asset) => asset.fileName.toLowerCase().includes(query))
		}
		return sortAssets(result, sort)
	}, [visibleAssets, collectionFilter, sort, search])

	const [selectedAssetId, setSelectedAssetId] = useAtom(assetHubSelectedAssetIdAtom, { store })

	const inputRef = useRef<HTMLInputElement>(null)

	const processFiles = async (files: File[]) => {
		if (files.length === 0 || !organizationId) {
			return
		}
		// Refuse a second batch while the first is still working. `inFlightBatchRef`
		// covers the async lifetime of a batch even when Send-to-background has
		// already cleared pendingUploads; `pendingUploads.length > 0` covers the
		// post-finally Done-acknowledgment phase, where async work is done but the
		// overlay is still up showing errors.
		if (inFlightBatchRef.current || pendingUploadsRef.current.length > 0) {
			return
		}
		inFlightBatchRef.current = true
		setIsInFlightBatch(true)
		const batchAbort = new AbortController()
		uploadAbortRef.current = batchAbort
		try {
			// Uploads always land in the 'uploaded' collection. If the user is filtered to
			// a different specific collection they wouldn't see the new arrivals, so flip
			// the filter for them. 'All' (null) already shows everything, leave that alone.
			if (collectionFilter !== null && collectionFilter !== 'uploaded') {
				setCollectionFilter('uploaded')
			}
			// Similarly, an active search would hide the newly uploaded files unless their
			// names happen to match — clear it so the user sees what they just uploaded.
			if (search.trim() !== '') {
				setSearch('')
			}

			if (files.length > 1) {
				logger.track('Assets: Bulk uploading', {
					data: { file_count: files.length },
				})
			}

			const uploadedAt = Date.now()
			const newUploads: PendingAssetUpload[] = files.map((file) => ({
				tempId: `pending-${++nextTempId}`,
				fileName: file.name,
				previewUrl: URL.createObjectURL(file),
				uploadedAt,
				status: 'uploading' as const,
			}))

			dismissedRef.current = false
			setPendingUploads(newUploads)

			const successfulUploads: { id: string; createdAt: number }[] = []

			// Throttle progress updates so a 50 MiB upload firing thousands of progress events
			// doesn't trigger thousands of re-renders. Only re-render when the ratio has moved
			// by at least 1% or when the upload completes.
			const lastReportedProgress = new Map<string, number>()

			await Promise.allSettled(
				files.map(async (file, index) => {
					const { tempId } = newUploads[index]

					const validationError = validateImageFile(file)
					if (validationError) {
						setPendingUploads((prev) =>
							prev.map((u) =>
								u.tempId === tempId
									? {
										...u,
										status: 'error' as const,
										error: formatFileValidationError(validationError),
									}
									: u
							)
						)
						return
					}

					try {
						const response = await uploadMutation.mutateAsync({
							organisation: organizationId,
							data: { media: file, collection: 'uploaded' },
							signal: batchAbort.signal,
							onProgress: (loaded, total) => {
								if (total <= 0) {
									return
								}
								// Clamp defensively — the spec says loaded <= total but a misbehaving
								// browser or transparent gzip layer could produce values outside [0,1]
								// and we'd otherwise show "147%" in the overlay.
								const ratio = Math.min(1, Math.max(0, loaded / total))
								const last = lastReportedProgress.get(tempId) ?? -1
								if (ratio < 1 && ratio - last < 0.01) {
									return
								}
								lastReportedProgress.set(tempId, ratio)
								setPendingUploads((prev) =>
									prev.map((u) => (u.tempId === tempId ? { ...u, progress: ratio } : u))
								)
							},
						})
						successfulUploads.push({
							id: response.id,
							createdAt: new Date(response.createdAt).getTime(),
						})
						setPendingUploads((prev) =>
							prev.map((u) => (u.tempId === tempId ? { ...u, status: 'success' as const } : u))
						)
					} catch (error) {
						// AbortError = the AssetGrid unmounted mid-upload. Component is gone,
						// so don't log to Sentry (intentional cancellation, not a failure) and
						// don't bother updating row state.
						if (error instanceof DOMException && error.name === 'AbortError') {
							return
						}
						// Don't log file.name — it's user-controlled and may contain PII.
						// fileType (MIME) already conveys what the extension would.
						logger.error(error, {
							tags: { component: 'AssetHub', action: 'upload' },
							data: { fileSize: file.size, fileType: file.type },
						})
						setPendingUploads((prev) =>
							prev.map((u) =>
								u.tempId === tempId
									? { ...u, status: 'error' as const, error: `Failed to upload "${file.name}"` }
									: u
							)
						)
					}
				}),
			)

			// Refetch once so the grid has the freshly-uploaded assets, then close the
			// overlay. We race the invalidation against a timeout: React Query retries 3x
			// with exponential backoff (~7-10s worst case), and 5s lets one retry cycle
			// recover from a transient blip while still bounding the wait. The refetch
			// continues in the background either way. The try/finally is defensive against
			// an unexpected reject.
			try {
				const raceResult = await Promise.race([
					queryClient
						.invalidateQueries({ queryKey: mediaImageIndexQueryKey() })
						.then(() => 'invalidated' as const),
					new Promise<'timeout'>((resolve) => setTimeout(() => resolve('timeout'), 5000)),
				])
				if (raceResult === 'timeout') {
					logger.track('Assets: Upload refetch did not settle in time, closing overlay early', {
						data: { file_count: files.length },
					})
				}
			} finally {
				// If the user clicked "Send to background", the overlay is already gone and
				// URLs are already revoked. Skip the post-completion UX — they opted out.
				if (!dismissedRef.current) {
					// Stash the post-completion work; the overlay's Done button will run it
					// when the user acknowledges. The button is disabled until all rows have
					// settled, so there's no risk of it firing mid-batch.
					pendingPostCompletionRef.current = () => {
						if (successfulUploads.length > 0) {
							const newest = successfulUploads.reduce((a, b) => (a.createdAt >= b.createdAt ? a : b))
							if (mode === 'picker') {
								setSelectedAssetId(newest.id)
							}
							setTimeout(() => {
								document
									.querySelector(`[data-asset-id="${newest.id}"]`)
									?.scrollIntoView({ behavior: 'smooth', block: 'center' })
							}, 0)
						}
					}
				}
			}
		} finally {
			// Releases the batch lock. The Done-acknowledgment phase (overlay still up
			// with errors) is covered by the pendingUploads.length guard, not this ref.
			inFlightBatchRef.current = false
			setIsInFlightBatch(false)
			if (uploadAbortRef.current === batchAbort) {
				uploadAbortRef.current = null
			}
		}
	}

	const handleDone = () => {
		const post = pendingPostCompletionRef.current
		pendingPostCompletionRef.current = null
		for (const upload of pendingUploadsRef.current) {
			URL.revokeObjectURL(upload.previewUrl)
		}
		setPendingUploads([])
		post?.()
	}

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const fileList = e.target.files
		if (!fileList) {
			return
		}
		const files = Array.from(fileList)
		e.target.value = ''
		void processFiles(files)
	}

	const handleSendToBackground = () => {
		dismissedRef.current = true
		for (const upload of pendingUploadsRef.current) {
			URL.revokeObjectURL(upload.previewUrl)
		}
		setPendingUploads([])
	}

	const handleAssetDeleted = (assetId: string) => {
		if (selectedAssetId === assetId) {
			setSelectedAssetId(null)
		}
	}

	const hasAnyUploading = pendingUploads.some((u) => u.status === 'uploading') || isInFlightBatch
	const isBusy = pendingUploads.length > 0 || isInFlightBatch
	// Backgrounded means: the lock is still held but the overlay isn't visible.
	// In that gap the user can't trigger another upload, so we surface a small
	// status pill in the sidebar so it doesn't feel like the platform is broken.
	const showBackgroundedIndicator = isInFlightBatch && pendingUploads.length === 0

	// Drag-and-drop upload. The counter handles bubble-fired enter/leave from nested
	// children — we only set isDraggingOver true on the first enter and false on the
	// matching final leave. Only "Files" drags count (not text, URLs, etc). While
	// uploads are in flight we still preventDefault (so the browser doesn't open the
	// file in a new tab) but show a "not-allowed" cursor and ignore the drop.
	const [isDraggingOver, setIsDraggingOver] = useState(false)
	const [draggingFileCount, setDraggingFileCount] = useState(0)
	const dragDepthRef = useRef(0)

	const isFileDrag = (e: DragEvent) => !!e.dataTransfer && e.dataTransfer.types.includes('Files')

	const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) {
			return
		}
		e.preventDefault()
		const wasDragging = dragDepthRef.current > 0
		dragDepthRef.current++
		if (!isBusy) {
			if (!wasDragging) {
				// Browsers expose item count (but not contents) during the drag.
				const items = e.dataTransfer?.items
				const count = items ? Array.from(items).filter((i) => i.kind === 'file').length : 0
				setDraggingFileCount(count)
			}
			setIsDraggingOver(true)
		}
	}

	const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) {
			return
		}
		e.preventDefault()
		if (e.dataTransfer) {
			e.dataTransfer.dropEffect = isBusy ? 'none' : 'copy'
		}
	}

	const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) {
			return
		}
		e.preventDefault()
		dragDepthRef.current = Math.max(0, dragDepthRef.current - 1)
		if (dragDepthRef.current === 0) {
			setIsDraggingOver(false)
			setDraggingFileCount(0)
		}
	}

	const handleDrop = (e: DragEvent<HTMLDivElement>) => {
		if (!isFileDrag(e)) {
			return
		}
		e.preventDefault()
		dragDepthRef.current = 0
		setIsDraggingOver(false)
		setDraggingFileCount(0)
		if (isBusy) {
			return
		}
		const dropped = e.dataTransfer?.files
		if (dropped && dropped.length > 0) {
			void processFiles(Array.from(dropped))
		}
	}

	return (
		<div
			className="relative h-full"
			onDragEnter={handleDragEnter}
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
		>
			<LibraryLayout
				title={t('assets.title')}
				showCloseButton={showCloseButton}
				onClose={onClose}
				isLoading={isLoading}
				sidebarStorageKey="asset-hub"
				toolbar={
					<div className="flex items-center gap-4 pr-4">
						<div className="p-2"></div>
						<div className="grow-1"></div>
						{showBackgroundedIndicator
							? (
								<div className="flex items-center gap-2 text-sm text-grey-500">
									<SubtleLoader width={14} />
									<span>Finishing uploads…</span>
								</div>
							)
							: null}
						<Tooltip
							content={
								<div className="text-center">
									<div>Upload your own images</div>
									<div className="text-[10px] text-grey-500 mt-0.5">
										JPG, PNG, AVIF (max 50MB)
									</div>
								</div>
							}
						>
							<Button
								size="short"
								type="committal"
								prefix="upload"
								onClick={() => inputRef.current?.click()}
								isLoading={hasAnyUploading}
							>
								Upload
							</Button>
						</Tooltip>

						<input
							ref={inputRef}
							type="file"
							accept="image/*"
							hidden={true}
							multiple={true}
							onChange={handleInputChange}
						/>
					</div>
				}
				sidebar={
					<div className="py-4 flex flex-col gap-4">
						<div className="pl-3 pr-3">
							<SearchFilter
								value={search}
								onChange={setSearch}
								placeholder={t('assets.searchPlaceholder')}
							/>
						</div>
						<div className="pl-3 pr-3">
							<div className="text-sm text-grey-500 tracking-loose mb-2">Sort by</div>
							<SelectFilter options={SORT_OPTIONS} value={sort} onChange={setSort} />
						</div>
						<div>
							<div className="text-sm text-grey-500 tracking-loose mb-2 pl-3 flex items-center gap-1.5">
								<CollectionsIcon />
								Collections
							</div>
							<FilterOption
								onClick={() => setCollectionFilter(null)}
								isActive={collectionFilter === null}
							>
								All
							</FilterOption>
							{Object.entries(collections).map(([collection, count]) => (
								<FilterOption
									key={collection}
									onClick={() => setCollectionFilter(collection)}
									isActive={collectionFilter === collection}
									counter={count}
								>
									{collection}
								</FilterOption>
							))}
						</div>
					</div>
				}
				committalBar={committalBar}
			>
				<div className="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] gap-8 p-8 min-h-[200px]">
					{filteredAssets.length === 0
						? (
							<div className="col-span-full flex items-center justify-center text-grey-400 text-sm py-16">
								{search.trim()
									? `No assets match "${search.trim()}".`
									: collectionFilter
									? `No assets in this collection.`
									: 'No assets yet. Upload some images to get started.'}
							</div>
						)
						: (
							filteredAssets.map((asset) => (
								<AssetCard
									key={asset.id}
									asset={asset}
									organizationId={organizationId}
									mode={mode}
									isSelected={selectedAssetId === asset.id}
									allowDelete={allowDelete}
									onSelect={() => {
										setSelectedAssetId(asset.id)
										onSelect?.(asset)
									}}
									onDeleted={() => handleAssetDeleted(asset.id)}
								/>
							))
						)}
				</div>
			</LibraryLayout>

			{isDraggingOver
				? (
					<div className="absolute inset-0 z-[150] flex items-center justify-center bg-blue-500/10 ring-4 ring-blue-500 ring-inset rounded-sm pointer-events-none">
						<div className="bg-white px-6 py-4 rounded-md shadow-lg text-base font-medium text-grey-800">
							{draggingFileCount > 0
								? `Drop to upload ${draggingFileCount} ${draggingFileCount === 1 ? 'file' : 'files'}`
								: 'Drop to upload'}
						</div>
					</div>
				)
				: null}

			{pendingUploads.length > 0
				? (
					<AssetUploadOverlay
						uploads={pendingUploads}
						onSendToBackground={handleSendToBackground}
						onDone={handleDone}
						isFinishing={isInFlightBatch}
					/>
				)
				: null}
		</div>
	)
}
